/**
 * Files on the clipboard, from BOTH surfaces a browser exposes (chat-thread PR-6,
 * 2026-09-09). `clipboardData.files` is empty for some copy sources — a screenshot
 * copied out of an image viewer, a file copied in a file manager on some platforms,
 * an image copied from another app on iPadOS — while the very same file is reachable
 * as a `DataTransferItem` of kind 'file'. The paste handler used to read `.files`
 * only, so those pastes fell through to the text branch and nothing was attached.
 * Read both, in order, and dedupe by identity so a file listed on both surfaces
 * attaches once.
 */

/** The subset of DataTransfer the reader needs — a plain object works in tests. */
export interface ClipboardDataLike {
  files?: ArrayLike<File | null | undefined> | null;
  items?: ArrayLike<{ kind?: string; getAsFile?: () => File | null } | null | undefined> | null;
}

function identityOf(f: File): string {
  return `${f.name}|${f.size}|${f.type}|${typeof f.lastModified === 'number' ? f.lastModified : ''}`;
}

export function filesFromClipboard(data: ClipboardDataLike | null | undefined): File[] {
  if (!data) return [];
  const out: File[] = [];
  const seen = new Set<string>();
  const push = (f: File | null | undefined) => {
    if (!f || typeof f !== 'object') return;
    const key = identityOf(f);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(f);
  };
  const files = data.files;
  if (files && typeof files.length === 'number') {
    for (let i = 0; i < files.length; i += 1) push(files[i]);
  }
  const items = data.items;
  if (items && typeof items.length === 'number') {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (!item || item.kind !== 'file' || typeof item.getAsFile !== 'function') continue;
      try {
        push(item.getAsFile());
      } catch {
        // a sandboxed / already-consumed item — nothing to attach from it
      }
    }
  }
  return out;
}
